<template>
  <div class="gw-period">
    <label class="rmr-table-shell__filter-label">Période</label>

    <rmr-segmented-control
      :value="mode"
      :items="periodItems"
      block
      @input="setMode"
    />

    <!-- Mounted only in "perso" mode: DateFilter self-initialises the store and
         the query-string on mount, so having both writers alive at once races. -->
    <date-filter
      v-if="mode === PERIOD_CUSTOM"
      class="gw-period__picker"
      store="newsRadar"
      period-type="past-and-today"
      range="1"
    />
  </div>
</template>

<script>
import { computed, onMounted, reactive } from "@vue/composition-api";

import DateFilter from "@/components/common/filters/DateFilter.vue";
import RmrSegmentedControl from "@/components/ui/actions/RmrSegmentedControl.vue";

import useProxy from "@/hooks/useProxy";
import useQueryString from "@/hooks/useQueryString";
import { getDate, getDateStr } from "@/utils/dates";

const PERIOD_24H = "24h";
const PERIOD_7D = "7j";
const PERIOD_CUSTOM = "perso";

// DateFilter resolves its param as URL_PARAM_NAMES[storeUpdater] || storeUpdater.
// There is no "updateDates" entry, so the param is literally "updateDates".
// We must write the same name to keep existing deep-links working.
const DATES_PARAM = "updateDates";

const PERIOD_SPANS = {
  [PERIOD_24H]: 1,
  [PERIOD_7D]: 7,
};

export default {
  name: "PeriodSegmented",
  components: {
    DateFilter,
    RmrSegmentedControl,
  },
  setup() {
    const { store } = useProxy();
    const { addQueryStringParam } = useQueryString();

    const page = reactive({
      isInitialized: false,
      mode: PERIOD_24H,
    });

    // =>  COMPUTED
    const dates = computed(() => store.getters["newsRadar/getDates"] || []);
    const mode = computed(() => page.mode);

    // =>  METHODS
    // Infer the segment from the stored range so a deep-link lands on the right
    // one instead of silently resetting the user's dates.
    const inferModeFromDates = (value) => {
      if (!Array.isArray(value) || value.length !== 2) {
        return PERIOD_24H;
      }

      const [start, end] = value;
      for (const [periodMode, span] of Object.entries(PERIOD_SPANS)) {
        if (start === getDateStr(getDate(-span)) && end === getDateStr()) {
          return periodMode;
        }
      }

      return PERIOD_CUSTOM;
    };

    const applyRange = (span) => {
      const value = [getDateStr(getDate(-span)), getDateStr()];
      store.dispatch("newsRadar/updateDates", value);

      if (page.isInitialized) {
        addQueryStringParam({
          param: DATES_PARAM,
          value,
          is_multiple: true,
        });
      }
    };

    const setMode = (nextMode) => {
      page.mode = nextMode;

      if (nextMode !== PERIOD_CUSTOM) {
        applyRange(PERIOD_SPANS[nextMode]);
      }
      // In custom mode DateFilter takes over on mount and writes both the store
      // and the query-string itself.
    };

    // =>  HOOKS
    onMounted(() => {
      page.mode = inferModeFromDates(dates.value);

      // Nothing is written on mount: DateFilter (custom mode) or the existing
      // store value owns the initial state.
      page.isInitialized = true;
    });

    // =>  DATA
    return {
      PERIOD_CUSTOM,
      mode,
      periodItems: [
        { value: PERIOD_24H, label: "24 h" },
        { value: PERIOD_7D, label: "7 jours" },
        { value: PERIOD_CUSTOM, label: "Perso." },
      ],
      setMode,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "../_global-watch.scss";

.gw-period {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

// Child component root: gets this component's scope id, no ::v-deep needed.
.rmr-segmented {
  min-height: $gw-control-height;
}

.gw-period__picker {
  margin-top: 4px;
  @include gw-legacy-field;
}
</style>
